<script setup lang="ts">
import { computed, markRaw, ref } from 'vue'

import clsx from 'clsx'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import draggable from 'vuedraggable'

import IconInputCheckbox from '@/components/atoms/icons/IconInputCheckbox.vue'
import IconInputDate from '@/components/atoms/icons/IconInputDate.vue'
import IconInputEmail from '@/components/atoms/icons/IconInputEmail.vue'
import IconInputHour from '@/components/atoms/icons/IconInputHour.vue'
import IconInputLocation from '@/components/atoms/icons/IconInputLocation.vue'
import IconInputTel from '@/components/atoms/icons/IconInputTel.vue'
import IconInputText from '@/components/atoms/icons/IconInputText.vue'
import IconInputTextarea from '@/components/atoms/icons/IconInputTextarea.vue'
import modal from '@/services/modal'
import notification from '@/services/notification'
import { useEditorBetaStore } from '@/stores/editor/beta'
import { useEditorPageStore } from '@/stores/editor/page'
import { useLayoutStore } from '@/stores/layout'

const props = defineProps<{
    element: any
    elementIndex: number
    sectionIndex: number
    sectionBackgroundColor: string
    elementParent?: any
    schema?: any
}>()

const betaStore = useEditorBetaStore()
const editorPageStore = useEditorPageStore()
const layoutStore = useLayoutStore()

const options = ref([
    { id: 1, name: 'Campo de texto', type: 'text', icon: markRaw(IconInputText) },
    { id: 2, name: 'Área de texto', type: 'textarea', icon: markRaw(IconInputTextarea) },
    { id: 3, name: 'E-mail', type: 'email', icon: markRaw(IconInputEmail) },
    { id: 4, name: 'Data', type: 'date', icon: markRaw(IconInputDate) },
    { id: 5, name: 'Telefone', type: 'tel', icon: markRaw(IconInputTel) },
    { id: 8, name: 'Selecionar Opção', type: 'option', icon: markRaw(IconInputCheckbox) },
    { id: 9, name: 'Horas', type: 'time', icon: markRaw(IconInputHour) },
    { id: 10, name: 'Localização', type: 'location', icon: markRaw(IconInputLocation) },
])
const elementId = computed(() => betaStore.getElementId(props.element))
const isActive = computed(() => betaStore.isElementActive(elementId.value))
const content = computed(() => props.element.frameContent?.content ?? [])
const isCustomParentBackgroundColor = computed(() => props.elementParent?.extra?.background_color && props.elementParent?.extra?.background_color !== '')
const backgroundColor = computed(() => {
    if (isCustomParentBackgroundColor.value) return props.elementParent.extra.background_color

    return editorPageStore.getSectionExtra(props.sectionIndex)?.background_color || props.sectionBackgroundColor
})
const textColor = computed(() => layoutStore.getAdaptiveTextColor(backgroundColor.value))
const dynamicStyle = computed(() => ({ color: textColor.value, stroke: textColor.value }))

const getOptionActive = (index: number) => options.value.find(option => option.type === content.value[index]?.type)

const updateFormContent = (fields: any[]) => {
    betaStore.patchElementBySchema(props.schema, {
        frameContent: {
            content: [...fields],
        },
    })
}

const patchField = (index: number, patch: Record<string, any>) => {
    const fields = [...content.value]
    fields[index] = { ...fields[index], ...patch }
    updateFormContent(fields)
}

const onClickNewField = () => {
    updateFormContent([
        ...content.value,
        {
            type: 'text',
            title: 'Novo campo',
            name: 'Novo campo',
            placeholder: '',
            required: false,
            size: 12,
            value: null,
            visible: true,
        },
    ])
}

const canDeleteInput = (index: number) => content.value.some((field: any, fieldIndex: number) => (
    fieldIndex !== index && (field.type === 'email' || field.type === 'tel')
))

const onRemoveInput = async (index: number) => {
    const result = await modal.confirmation('Confirmar Exclusão', 'Deseja realmente excluir este campo?', {
        type: 'danger',
        textConfirm: 'Sim, excluir',
        textCancel: 'Não, voltar',
    })

    if (!result) return

    if (!canDeleteInput(index)) {
        notification.simple('O formulário deve ter ao menos um campo de E-mail ou Telefone', 'error')
        return
    }

    const fields = [...content.value]
    fields.splice(index, 1)
    updateFormContent(fields)
}

const onRemoveInputValue = (index: number, indexValue: number) => {
    const values = [...(content.value[index]?.value ?? [])]
    values.splice(indexValue, 1)
    patchField(index, { value: values })
}

const addOption = (index: number) => {
    patchField(index, { value: [...(content.value[index]?.value ?? []), ''] })
}

const onChangeOptionValue = (index: number, indexValue: number, value: string) => {
    const values = [...(content.value[index]?.value ?? [])]
    values[indexValue] = value
    patchField(index, { value: values })
}

const onEndDraggable = () => {
    updateFormContent([...content.value])
}
</script>

<template>
    <div
        tabindex="0"
        :id="'betaEditorForm-' + elementIndex"
        :class="clsx('editor-form-beta')"
        :data-active="isActive"
        :schema="JSON.stringify(schema)"
        :style="dynamicStyle"
    >
        <div class="space-y-6 p-4">
            <h2 class="text-lg font-bold">Campos do Formulário</h2>

            <draggable :list="content" item-key="index" handle=".handle-drag" class="space-y-6" @change="onEndDraggable">
                <template #item="{ element, index }">
                    <Disclosure>
                        <div class="w-full rounded-lg border-[1.5px] border-[#F5F5F5] p-6">
                            <DisclosureButton class="flex w-full items-center justify-between">
                                <div class="flex items-center space-x-2">
                                    <button class="handle-drag cursor-ns-resize" type="button">
                                        <svg class="h-8 w-8 stroke-[var(--main-color)]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                            <path d="M15.6 15.6L12 19.2L8.39999 15.6M8.39999 8.39999L12 4.79999L15.6 8.39999" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                        </svg>
                                    </button>
                                    <span>{{ element.title }}</span>
                                </div>

                                <button type="button" @click.stop="onRemoveInput(index)">
                                    <svg :style="dynamicStyle" xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none" stroke="currentColor">
                                        <path d="M0.888885 3.82353H15.1111M5.33333 1H10.6667M11.1111 17H4.88889C3.90705 17 3.11111 16.1572 3.11111 15.1176L2.70524 4.80389C2.6842 4.26918 3.08792 3.82353 3.59336 3.82353H12.4066C12.9121 3.82353 13.3158 4.26918 13.2948 4.80389L12.8889 15.1176C12.8889 16.1572 12.0929 17 11.1111 17Z" stroke-width="1.5" stroke-linecap="round" />
                                    </svg>
                                </button>
                            </DisclosureButton>

                            <DisclosurePanel class="w-full space-y-6 px-10">
                                <div class="mt-6 flex flex-col">
                                    <label class="text-sm font-semibold">Tipo do Campo</label>
                                    <select class="rounded border-2 border-[#F5F5F5] bg-transparent px-4 py-3 outline-none" :value="element.type" @change="(event: any) => patchField(index, { type: event.target.value })">
                                        <option v-for="option in options" :key="option.id" :value="option.type">
                                            {{ option.name }}
                                        </option>
                                    </select>
                                </div>

                                <div class="flex flex-col">
                                    <label class="text-sm font-semibold">Nome do Campo</label>
                                    <input v-model="element.title" type="text" class="rounded border-2 border-[#F5F5F5] bg-transparent px-4 py-3 outline-none" placeholder="Escreva o nome do campo" @input="(event: any) => patchField(index, { title: event.target.value })">
                                </div>

                                <div v-if="element.type !== 'option'" class="flex flex-col">
                                    <label class="text-sm font-semibold">Texto de Exemplo*</label>
                                    <input v-model="element.placeholder" type="text" class="rounded border-2 border-[#F5F5F5] bg-transparent px-4 py-3 outline-none" placeholder="Escreva o texto de exemplo" @input="(event: any) => patchField(index, { placeholder: event.target.value })">
                                </div>

                                <div v-if="element.type === 'option'" class="flex flex-col">
                                    <label class="text-sm font-semibold">Adicione as opções</label>
                                    <div class="flex flex-col space-y-1">
                                        <div v-for="(value, indexValue) in element.value" :key="indexValue" class="relative flex w-full justify-between">
                                            <input :value="value" type="text" class="w-full rounded px-4 py-3 outline-none" placeholder="Ex: Opção 1" @input="(event: any) => onChangeOptionValue(index, indexValue, event.target.value)">
                                            <button type="button" class="bg-transparent outline-none" @click="onRemoveInputValue(index, indexValue)">
                                                <svg :style="dynamicStyle" xmlns="http://www.w3.org/2000/svg" width="16" height="18" viewBox="0 0 16 18" fill="none" stroke="currentColor">
                                                    <path d="M0.888885 3.82353H15.1111M5.33333 1H10.6667M11.1111 17H4.88889C3.90705 17 3.11111 16.1572 3.11111 15.1176L2.70524 4.80389C2.6842 4.26918 3.08792 3.82353 3.59336 3.82353H12.4066C12.9121 3.82353 13.3158 4.26918 13.2948 4.80389L12.8889 15.1176C12.8889 16.1572 12.0929 17 11.1111 17Z" stroke-width="1.5" stroke-linecap="round" />
                                                </svg>
                                            </button>
                                        </div>

                                        <button type="button" class="flex items-center px-4 py-3" @click="addOption(index)">
                                            <svg class="h-6 w-6" :style="dynamicStyle" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                                <path d="M12 7.19995L12 16.8M16.8 12L7.2 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <label class="flex items-center gap-2 text-sm font-semibold">
                                    <input type="checkbox" :checked="element.required" @change="(event: any) => patchField(index, { required: event.target.checked })">
                                    Obrigatório
                                </label>
                            </DisclosurePanel>
                        </div>
                    </Disclosure>
                </template>
            </draggable>

            <button type="button" class="flex items-center justify-center rounded-full border-2 border-[var(--main-color)] px-6 py-3" @click="onClickNewField">
                <span class="mr-2 text-base font-bold text-[var(--main-color)]">Novo Campo</span>
                <svg class="h-6 w-6 stroke-[var(--main-color)]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 7.19995L12 16.8M16.8 12L7.2 12" stroke-width="2" stroke-linecap="round" />
                </svg>
            </button>
        </div>
    </div>
</template>

<style scoped>
.editor-form-beta {
    @apply relative my-3 border-2 border-transparent bg-transparent outline-none transition-all duration-1000 ease-in-out hover:border-[#D7E3F4] hover:shadow-[0_0_0_2px_rgba(215,227,244,0.22)];
}

.editor-form-beta[data-active='true'] {
    @apply border-[#8EB3E8] shadow-[0_0_0_3px_rgba(142,179,232,0.24)];
}
</style>
